/**
 * Буцаалт, солилцоо — `/returns`.
 *
 * УРСГАЛ
 *  1. Худалдан авагчийг БАТАЛГААЖУУЛАХ — шошго · баримт · утас
 *  2. Буцаах мөрөө сонгох
 *  3. Хоёрын нэг (2026-10-03):
 *     · СОЛИЛЦОО — шинэ гутал уншуулна; зөрүүг тооцно (үнэтэй бол нэмж
 *       төлнө, хямд бол буцааж олгоно)
 *     · БҮТЭН БУЦААЛТ — гутлын төлсөн бүтэн дүнг буцаана
 *     Аль алинд нь мөнгө ЯМАР хэлбэрээр явсныг бүртгэнэ — кассын хаалт,
 *     буцаалтын тайлан хэлбэр тус бүрээр тоолно.
 *
 * ⚠️ СОЛИЛЦОО нь `POST /orders`-оор явна, тусдаа endpoint-оор БИШ.
 *    Захиалга өөрчлөгдөшгүй (CLAUDE.md №2) тул солилцоо гэдэг нь
 *    «хуучин мөрийг солих» биш, ХОЁР баримт: буцаалт + шинэ захиалга.
 *    Сервер хоёуланг нь НЭГ транзакцад бичнэ — дунд нь тасарвал гутал
 *    буцаагдчихаад орлого бүртгэгдэхгүй үлдэх ёсгүй.
 *
 * ⚠️ Зөрүүг дэлгэц, сервер хоёр ИЖИЛ функцээр бодно (`exchangeSettlement`).
 *
 * ХӨНГӨЛӨЛТ, УРАМШУУЛАЛ (2026-10-11) — шинэ гутлын мөрийн `discountMnt`.
 *    Хязгаар нь зөвхөн шинэ гутлын үнэ (сервертэй ижил): зөрүүнээс их
 *    байвал шинэ гутал хуучнаасаа хямд болж, касс зөрүүг БУЦААЖ олгоно.
 *    Мөрийн хөнгөлөлт тул орлого, урамшуулал, баримт, кассын хаалт энгийн
 *    борлуулалтын хөнгөлөлттэй адил тооцно.
 *
 * ⚠️ Буцаасан гутал `RETURNED` төлөвт орж, ДАХИН ЗАРАГДАНА
 *    (`lib/sellable.ts`). «Буцаагдаж байсан» гэдэг нь түүхэнд үлдэнэ.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exchangeSettlement, formatDate, formatLocation, formatMnt, formatSerialDisplay,
  isValidSerial, moneyInput, normalizeSerialInput, returnLocationChoices, type ReturnDispositionKey,
} from '@boots/domain';
import { ApiError, api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PAYMENT_LABEL, type ItemListRow, type Location } from '../lib/types';
import { REFUND_METHOD_OPTIONS, defaultRefundMethod } from '../lib/refunds';
import {
  Button, Empty, ErrorNote, Field, Input, OkNote, Select, Spinner, Tabs, Textarea,
} from '../components/ui';
import { Section } from '../components/section';

type By = 'serial' | 'code' | 'phone';

const BY_TABS: { key: By; label: string }[] = [
  { key: 'serial', label: 'Шошго' },
  { key: 'code', label: 'Баримт' },
  { key: 'phone', label: 'Утас' },
];

const PLACEHOLDER: Record<By, string> = {
  serial: 'Баркод уншуулах эсвэл 10 цифр',
  code: 'S-2026-004521',
  phone: '99112233',
};

/** `GET /returns/lookup`-ийн мөр. */
interface ReturnableLine {
  id: string;
  priceMnt: string;
  discountMnt: string;
  lineTotalMnt: string;
  /** БОДИТООР төлсөн — хуучин баримтын «Захиалгын хөнгөлөлт» хасагдсан. Буцаах, солих дүн үүнээс (сервертэй ижил). */
  paidMnt: string;
  warrantyEndsAt: string;
  snapshot: { brand?: string; modelName?: string; size?: number } | null;
  item: { id: string; serial: string | null; size: number; status: string };
  order: {
    id: string; code: string; soldAt: string; storeId: string;
    /** Анх төлсөн хэлбэр — буцаах хэлбэрийн анхдагч */
    payment: string;
    store: { id: string; name: string };
    soldBy: { fullName: string };
    customer: { id: string; name: string | null; phone: string } | null;
  };
}

interface LookupResult { by: By; lines: ReturnableLine[] }

const DISPOSITIONS = [
  { value: 'RESTOCKED', label: 'Дахин зарна' },
  { value: 'DEFECTIVE', label: 'Гэмтэлтэй — хорио цээр' },
  { value: 'RETURN_TO_SUPPLIER', label: 'Нийлүүлэгч рүү буцаана' },
  { value: 'WRITTEN_OFF', label: 'Актална' },
];

export function ReturnsPage() {
  const { hasPerm } = useAuth();
  const [by, setBy] = useState<By>('serial');
  const [text, setText] = useState('');
  const [query, setQuery] = useState<{ by: By; value: string } | null>(null);
  const [picked, setPicked] = useState<ReturnableLine | null>(null);

  const canRefund = hasPerm('orders.refund');

  const lookup = useQuery({
    queryKey: ['return-lookup', query?.by, query?.value],
    enabled: query !== null,
    retry: false,
    queryFn: () => {
      const v = query!.value;
      const param = query!.by === 'serial' ? normalizeSerialInput(v) : v;
      return api<Envelope<LookupResult>>(
        `/returns/lookup?${query!.by}=${encodeURIComponent(param)}`,
      );
    },
  });

  function search() {
    const value = text.trim();
    if (!value) return;
    if (by === 'serial' && !isValidSerial(value)) {
      setQuery({ by, value }); // сервер илүү тодорхой алдаа өгнө
      return;
    }
    setPicked(null);
    setQuery({ by, value });
  }

  return (
    <div className="flex flex-col gap-3">
      {/* ─────────── 1. Баталгаажуулах ─────────── */}
      <Section
        title="Худалдан авагчийг олох"
        note="шошго нь гутлын дотор талд — олдохгүй бол баримт эсвэл утсаар"
      >
        <div className="flex flex-wrap items-center gap-2.5">
          <Tabs tabs={BY_TABS} value={by} onChange={(v) => { setBy(v); setText(''); setQuery(null); setPicked(null); }} />
          <form
            className="flex flex-1 items-center gap-2"
            onSubmit={(e) => { e.preventDefault(); search(); }}
          >
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={PLACEHOLDER[by]}
              inputMode={by === 'code' ? 'text' : 'numeric'}
              autoFocus
              className="num max-w-72"
              aria-label={BY_TABS.find((t) => t.key === by)?.label}
            />
            <Button type="submit" disabled={!text.trim()}>Хайх</Button>
          </form>
        </div>

        {lookup.isFetching ? <Spinner /> : null}
        {lookup.error ? <ErrorNote>{(lookup.error as Error).message}</ErrorNote> : null}
        {lookup.data?.data.lines.length === 0 ? (
          <Empty>Буцаах боломжтой худалдан авалт олдсонгүй</Empty>
        ) : null}

        {lookup.data && lookup.data.data.lines.length > 0 ? (
          <ul className="flex flex-col gap-1.5">
            {lookup.data.data.lines.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => setPicked(l)}
                  aria-current={picked?.id === l.id}
                  className={`flex w-full flex-wrap items-baseline gap-x-3 gap-y-1 rounded-well
                    border px-3.5 py-2.5 text-left transition ${
                    picked?.id === l.id
                      ? 'border-accent-300 bg-accent-100'
                      : 'border-hair bg-well hover:border-accent-300'
                  }`}
                >
                  <span className="num">{l.item.serial ? formatSerialDisplay(l.item.serial) : '—'}</span>
                  <span className="text-pretty">
                    {l.snapshot?.brand} {l.snapshot?.modelName}
                  </span>
                  <span className="num text-meta text-ink-2">размер {l.item.size}</span>
                  <span className="num ml-auto">{formatMnt(l.paidMnt)}</span>
                  <span className="w-full text-label text-ink-3">
                    <span className="num">{l.order.code}</span> · {formatDate(l.order.soldAt)} ·{' '}
                    {l.order.store.name} · {l.order.customer?.phone ?? 'нэргүй'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </Section>

      {/* ─────────── 2. Үйлдэл ─────────── */}
      {picked ? (
        canRefund ? (
          <Act line={picked} onDone={() => { setPicked(null); setQuery(null); setText(''); }} />
        ) : (
          <Section title="Үйлдэл">
            <Empty>Буцаалт бүртгэх эрх алга</Empty>
          </Section>
        )
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------ Солилцоо / бүтэн буцаалт
type Mode = 'exchange' | 'refund';

const MODES: { key: Mode; label: string }[] = [
  { key: 'exchange', label: 'Солилцоо — зөрүү тооцох' },
  { key: 'refund', label: 'Төлбөрийг бүтнээр буцаах' },
];

function Act({ line, onDone }: { line: ReturnableLine; onDone: () => void }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('exchange');
  const [reasonId, setReasonId] = useState('');
  const [disposition, setDisposition] = useState<ReturnDispositionKey>('RESTOCKED');
  const [locationId, setLocationId] = useState('');
  const [notes, setNotes] = useState('');
  const [refundMethod, setRefundMethod] = useState<string>(defaultRefundMethod(line.order.payment));
  const [done, setDone] = useState<string | null>(null);

  const reasons = useQuery({
    queryKey: ['lookups', 'RETURN_REASON'],
    queryFn: () =>
      api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=RETURN_REASON'),
  });

  // Хаашаа тавихыг ажилтан сонгоно. Заагаагүй бол сервер
  // `disposition`-оос автоматаар олно.
  const locs = useQuery({
    queryKey: ['locations'],
    queryFn: () => api<Envelope<Location[]>>('/ref/locations'),
    staleTime: 5 * 60_000,
  });

  const shared = {
    reasonLookupId: reasonId,
    disposition,
    ...(locationId ? { locationId } : {}),
    ...(notes.trim() ? { notes: notes.trim() } : {}),
  };

  // Бүтэн буцаалт — дүнг сервер мөрийн бүтэн дүнгээр тавина
  const doRefund = useMutation({
    mutationFn: () =>
      api(`/order-lines/${line.id}/return`, {
        method: 'POST',
        body: { ...shared, refundMethod },
      }),
    onSuccess: async () => {
      setDone(`Бүтэн буцаалт бүртгэгдлээ · ${formatMnt(line.paidMnt)} ${PAYMENT_LABEL[refundMethod] ?? ''}`);
      await qc.invalidateQueries({ queryKey: ['items'] });
      await qc.invalidateQueries({ queryKey: ['returns'] });
    },
  });

  const title = mode === 'exchange' ? 'Солилцоо' : 'Бүтэн буцаалт';
  const subtitle = `${line.snapshot?.brand ?? ''} ${line.snapshot?.modelName ?? ''} · ${formatMnt(line.paidMnt)}`;

  // Бүртгэгдмэгц маягт хаагдана (2026-10-12): өмнө нь мэдэгдэл хэсгийн ДЭЭД
  // талд гарч, доош гүйлгэсэн ажилтан хараагүй, идэвхтэй хэвээр товчийг дахин
  // дарж «SOLD төлөвтэй» алдаа авдаг байв.
  if (done) {
    return (
      <Section title={title} subtitle={subtitle}>
        <OkNote>{done}</OkNote>
        <Button variant="ghost" onClick={onDone}>Дуусгах</Button>
      </Section>
    );
  }

  return (
    <Section
      title={title}
      subtitle={subtitle}
      actions={<Tabs value={mode} onChange={setMode} tabs={MODES} />}
    >
      <div className="grid items-start gap-x-4 gap-y-3 sm:grid-cols-2">
        <Field label="Шалтгаан">
          <Select
            value={reasonId}
            onChange={setReasonId}
            placeholder={reasons.isPending ? 'Ачаалж байна…' : 'Сонгоно уу'}
            options={(reasons.data?.data ?? []).map((r) => ({ value: r.id, label: r.labelMn }))}
          />
        </Field>

        <Field label="Гутлыг яах вэ">
          <Select
            value={disposition}
            onChange={(v) => {
              setDisposition(v as ReturnDispositionKey);
              // Өмнөх сонголт шинэ үйлдэлд тохирохгүй байж болно
              setLocationId('');
            }}
            options={DISPOSITIONS}
          />
        </Field>

        {/*
          Зөвхөн энэ үйлдэлд тохирох байршил (№11): дахин зарах бол ЭНЭ
          салбарынх, гэмтэлтэй бол хорио цээр/засвар, актлах бол устгасан.
          Сонгохгүй бол сервер өөрөө олно — өмнөх зан хэвээр.
        */}
        <Field label="Хаашаа тавих">
          <Select
            value={locationId}
            onChange={setLocationId}
            placeholder="Автоматаар (төлвөөс хамаарна)"
            options={returnLocationChoices(locs.data?.data ?? [], disposition, line.order.storeId).map((l) => ({
              value: l.id,
              label: formatLocation(l),
            }))}
          />
        </Field>

        <Field label="Тэмдэглэл">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>

      {mode === 'refund' ? (
        <div className="flex flex-col gap-3 border-t border-hair pt-3.5">
          <div className="flex flex-wrap items-end gap-3">
            <Field label="Мөнгийг буцааж өгөх хэлбэр">
              <Select value={refundMethod} onChange={setRefundMethod} options={REFUND_METHOD_OPTIONS} />
            </Field>
            <span className="text-meta text-ink-2">
              Буцаах дүн <span className="num font-semibold text-ink">{formatMnt(line.paidMnt)}</span>
              {' '}· худалдан авагчийн төлсөн дүн
            </span>
            <Button
              className="ml-auto"
              onClick={() => doRefund.mutate()}
              disabled={!reasonId || doRefund.isPending}
            >
              {doRefund.isPending ? 'Бүртгэж байна…' : 'Бүтэн буцаалт бүртгэх'}
            </Button>
          </div>
          {!reasonId ? <p className="text-label text-ink-3">Эхлээд шалтгаанаа сонгоно уу</p> : null}
        </div>
      ) : (
        <Exchange
          line={line}
          shared={shared}
          ready={Boolean(reasonId)}
          onDone={(msg) => setDone(msg)}
        />
      )}

      {doRefund.error ? <ErrorNote>{(doRefund.error as Error).message}</ErrorNote> : null}
    </Section>
  );
}

// ------------------------------------------------------------ Солих гутал
function Exchange({
  line, shared, ready, onDone,
}: {
  line: ReturnableLine;
  shared: Record<string, unknown>;
  ready: boolean;
  onDone: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [serial, setSerial] = useState('');
  const [found, setFound] = useState<ItemListRow | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [payment, setPayment] = useState<string>('CASH');
  const [refundMethod, setRefundMethod] = useState<string>(defaultRefundMethod(line.order.payment));
  const [discount, setDiscount] = useState('');
  const [busy, setBusy] = useState(false);

  // Хөнгөлөлт шинэ гутлын үнээс хасагдана — зөрүү нь ТҮҮНИЙ дараа бодогдоно
  const discountMnt = moneyInput(discount);
  const discounted = Number(discountMnt) > 0;
  const tooMuch = found !== null && Number(discountMnt) > Number(found.priceMnt);
  const newTotal = found && !tooMuch ? Number(found.priceMnt) - Number(discountMnt) : null;

  // Сервертэй ИЖИЛ дүрэм (`@boots/domain`) — дэлгэц, баримт хоёр зөрөхгүй
  const settle = newTotal !== null ? exchangeSettlement(line.paidMnt, newTotal) : null;
  const extra = settle?.extraMnt.greaterThan(0) ? settle.extraMnt.toFixed(2) : null;
  const refund = settle?.refundMnt.greaterThan(0) ? settle.refundMnt.toFixed(2) : null;

  async function find() {
    setLookupError(null);
    setFound(null);
    setDiscount('');
    const clean = normalizeSerialInput(serial);
    if (!isValidSerial(clean)) {
      setLookupError('Гутлын код буруу байна');
      return;
    }
    setBusy(true);
    try {
      const res = await api<Envelope<ItemListRow>>(`/items/by-serial/${clean}`);
      setFound(res.data);
    } catch (e) {
      setLookupError(e instanceof ApiError ? e.message : 'Гутал олдсонгүй');
    } finally {
      setBusy(false);
    }
  }

  const confirm = useMutation({
    mutationFn: () =>
      api<Envelope<{ code: string }>>('/orders', {
        method: 'POST',
        body: {
          storeId: line.order.storeId,
          // Зөвхөн НЭМЖ төлөх зөрүүнд хамаарна — хуучин гутлын үнийг сервер
          // «Солилцоо» төлбөр болгоно
          payment,
          lines: [{ itemId: found!.id, discountMnt }],
          ...(line.order.customer ? { customerPhone: line.order.customer.phone } : {}),
          exchange: { orderLineId: line.id, ...shared, ...(refund ? { refundMethod } : {}) },
        },
      }),
    onSuccess: async (res) => {
      onDone(
        `Солилцоо бүртгэгдлээ · ${res.data.code}` +
          (discounted ? ` · хөнгөлөлт ${formatMnt(discountMnt)}` : '') +
          (refund ? ` · ${formatMnt(refund)} буцааж өгсөн (${PAYMENT_LABEL[refundMethod] ?? refundMethod})` : ''),
      );
      await qc.invalidateQueries({ queryKey: ['items'] });
      await qc.invalidateQueries({ queryKey: ['orders'] });
      await qc.invalidateQueries({ queryKey: ['returns'] });
    },
  });

  const notSellable = found !== null && !['AVAILABLE', 'RETURNED'].includes(found.status);

  return (
    <div className="flex flex-col gap-3 border-t border-hair pt-3.5">
      <Field label="Солих гутлын шошго">
        <div className="flex items-center gap-2">
          <Input
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void find(); } }}
            placeholder="Баркод уншуулах"
            inputMode="numeric"
            className="num max-w-56"
          />
          <Button variant="ghost" onClick={() => void find()} disabled={!serial.trim() || busy}>
            {busy ? 'Хайж байна…' : 'Олох'}
          </Button>
        </div>
      </Field>

      {lookupError ? <ErrorNote>{lookupError}</ErrorNote> : null}

      {found ? (
        <div className="flex flex-col gap-2 rounded-well bg-well px-3.5 py-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="num">{formatSerialDisplay(found.serial ?? '')}</span>
            <span className="text-pretty">{found.model.brand} {found.model.name}</span>
            <span className="num text-meta text-ink-2">размер {found.size}</span>
            <span className="num ml-auto">{formatMnt(found.priceMnt)}</span>
          </div>

          {/* Хөнгөлөлт, урамшуулал — дүнг гараар, зөрүү доор өөрөө шинэчлэгдэнэ */}
          <div className="flex flex-wrap items-end gap-x-3 gap-y-1.5 border-t border-hair pt-2.5">
            <Field label="Хөнгөлөлт, урамшуулал ₮">
              <Input
                value={discount}
                onChange={(e) => setDiscount(e.target.value.replace(/\D/g, ''))}
                placeholder="0"
                inputMode="numeric"
                className="num max-w-40 text-right"
              />
            </Field>
            {discounted && newTotal !== null ? (
              <span className="text-meta text-ink-2">
                Шинэ гутал <span className="num line-through">{formatMnt(found.priceMnt)}</span>
                {' → '}
                <span className="num font-semibold text-ink">{formatMnt(newTotal)}</span>
              </span>
            ) : (
              <span className="text-label text-ink-3">Үнийн зөрүүнээс автоматаар хасагдана</span>
            )}
          </div>
          {tooMuch ? <ErrorNote>Хөнгөлөлт шинэ гутлын үнээс их байна</ErrorNote> : null}

          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-meta">
            <span className="text-ink-2">Буцаах гутал</span>
            <span className="num">{formatMnt(line.paidMnt)}</span>
            <span className="text-ink-3">→</span>
            {tooMuch ? (
              <span className="text-ink-3">—</span>
            ) : extra ? (
              <>
                <span className="text-ink-2">Нэмж төлөх</span>
                <span className="num font-semibold text-alert-ink">{formatMnt(extra)}</span>
              </>
            ) : refund ? (
              <>
                <span className="text-ink-2">Буцааж олгох</span>
                <span className="num font-semibold text-accent-700">{formatMnt(refund)}</span>
              </>
            ) : (
              <span className="text-ink-2">Зөрүүгүй — мөнгө хөдлөхгүй</span>
            )}
          </div>

          {notSellable ? (
            <ErrorNote>Энэ гутал {found.status} төлөвтэй — солих боломжгүй</ErrorNote>
          ) : null}
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-3">
        {extra ? (
          <Field label="Зөрүүг хэрхэн төлөх">
            <Select value={payment} onChange={setPayment} options={REFUND_METHOD_OPTIONS} />
          </Field>
        ) : null}
        {refund ? (
          <Field label="Зөрүүг буцааж өгөх хэлбэр">
            <Select value={refundMethod} onChange={setRefundMethod} options={REFUND_METHOD_OPTIONS} />
          </Field>
        ) : null}
        <Button
          className="ml-auto"
          onClick={() => confirm.mutate()}
          disabled={!found || !ready || notSellable || tooMuch || confirm.isPending}
        >
          {confirm.isPending ? 'Бүртгэж байна…' : 'Солилцоо баталгаажуулах'}
        </Button>
      </div>

      {!ready ? <p className="text-label text-ink-3">Эхлээд шалтгаанаа сонгоно уу</p> : null}
      {confirm.error ? <ErrorNote>{(confirm.error as Error).message}</ErrorNote> : null}
      <p className="text-label text-ink-3">
        Хуучин баримт хэвээр үлдэж, шинэ баримт үүснэ. Шинэ гутлын баталгаа өнөөдрөөс эхэлнэ.
      </p>
    </div>
  );
}
